'use client'

import { useMemo } from 'react'

import { FilterDropdownSize, Icon, IconComponent } from '@graftcode/design-system'

import { TIME_RANGE_FILTER_TEST_IDS } from '@apps/service-map/service-map-test-ids.const'

import styles from './TimeRangeFilterTrigger.module.scss'

export type TimeRangeFilterTriggerProps = {
  id?: string
  triggerLabel: string
  disabled: boolean
  isOpen: boolean
  ariaTitle: string
  chipActive?: boolean
}

export const TimeRangeFilterTrigger = function TimeRangeFilterTrigger({
  ref,
  id,
  triggerLabel,
  disabled,
  isOpen,
  ariaTitle,
  chipActive = false,
  ...radixTriggerProps
}: TimeRangeFilterTriggerProps & { ref?: React.RefObject<HTMLButtonElement | HTMLDivElement | null> }) {
  const triggerClass = useMemo(() => {
    const classes = ['filter-dropdown', `filter-dropdown--${FilterDropdownSize.M}`]
    if (chipActive) {
      classes.push('filter-dropdown--chip')
    }
    if (disabled) {
      classes.push('filter-dropdown--disabled')
    }
    return classes.join(' ')
  }, [chipActive, disabled])

  if (chipActive) {
    return (
      <button
        ref={ref as React.RefObject<HTMLButtonElement | null>}
        type="button"
        id={id}
        data-testid={TIME_RANGE_FILTER_TEST_IDS.trigger}
        {...radixTriggerProps}
        className={triggerClass}
        aria-label={ariaTitle}
        aria-haspopup="dialog"
        disabled={disabled}
      >
        <span className="filter-dropdown__label">{triggerLabel}</span>
      </button>
    )
  }

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement | null>}
      id={id}
      data-testid={TIME_RANGE_FILTER_TEST_IDS.trigger}
      {...radixTriggerProps}
      className={triggerClass}
      aria-label={ariaTitle}
      aria-haspopup="dialog"
      aria-disabled={disabled || undefined}
    >
      <span className="filter-dropdown__label">{triggerLabel}</span>
      <span className="filter-dropdown__chevron">
        <IconComponent icon={isOpen ? Icon.CaretUpFilled : Icon.CaretDownFilled} />
      </span>
    </div>
  )
}

export function TimeRangeFilterClearButton({ disabled, onClear }: { disabled: boolean; onClear: () => void }) {
  return (
    <button
      type="button"
      className={styles.clear}
      aria-label="Clear filter"
      disabled={disabled}
      onPointerDown={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onClear()
      }}
    >
      <IconComponent icon={Icon.DismissRegular} />
    </button>
  )
}
